import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// `preventOutsideClose` — when true, tapping/clicking outside the dialog
// will NOT close it. Critical for form dialogs that contain portal-based
// inputs (Radix <Select>, <Popover>, date pickers) — on mobile, a tap on
// a portal item can bubble up as an "outside" tap and wipe the form.
// The dialog can still be closed via the X button, explicit Cancel, or a
// dirty-check wrapper provided by the caller.
//
// Mobile responsiveness (v77.18.3):
//   • On screens < sm (< 640px): the dialog becomes a true full-screen sheet.
//     It anchors to all four edges, takes 100dvh (dynamic viewport height,
//     correct under iOS/Android browser chrome) and the inner content area
//     scrolls with touch. Header and Footer are sticky so the title and
//     submit/cancel actions are always reachable without finger gymnastics.
//   • On screens ≥ sm: the dialog is a centered modal capped at
//     calc(100dvh - 4rem) with the same internal scroll + sticky chrome.
//   • Safe-area insets are respected on iOS notch + Android nav bar.
const DialogContent = React.forwardRef(({
  className,
  children,
  preventOutsideClose,
  onPointerDownOutside,
  onInteractOutside,
  hideCloseButton = false,
  ...props
}, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // ---------- Base (applies to all sizes) ----------
        "fixed z-50 flex flex-col bg-background shadow-lg duration-200",
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",

        // ---------- Desktop default (sm and up) ----------
        // Centered, max-w-lg by default, capped height with internal scroll.
        // Callers can still override max-w via `className` (max-w-md, max-w-2xl, etc.).
        "left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]",
        // v77.99.42 — Tighter mobile padding (p-4 ≈ 16px each side
        // instead of p-6's 24px) so forms at 360px width get 32px
        // more usable space without overflow. Desktop unchanged.
        "w-full max-w-lg max-h-[calc(100dvh-4rem)] rounded-lg border p-4 sm:p-6 gap-4",
        "sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95",

        // Caller-supplied classes (max-w override etc.) come BEFORE the
        // mobile overrides so we can reliably win on mobile.
        className,

        // ---------- Mobile (<640px) full-screen sheet ----------
        // These rules are last so tailwind-merge keeps them as the
        // winners for the max-sm breakpoint.
        "max-sm:inset-0 max-sm:left-0 max-sm:top-0 max-sm:translate-x-0 max-sm:translate-y-0",
        "max-sm:w-full max-sm:max-w-none max-sm:h-[100dvh] max-sm:max-h-[100dvh]",
        "max-sm:rounded-none max-sm:border-0 max-sm:p-0 max-sm:gap-0",
      )}
      onPointerDownOutside={(e) => {
        if (preventOutsideClose) e.preventDefault();
        onPointerDownOutside?.(e);
      }}
      onInteractOutside={(e) => {
        if (preventOutsideClose) e.preventDefault();
        onInteractOutside?.(e);
      }}
      {...props}>
      {/*
        Scrollable body. We move the actual scroll onto an inner wrapper so
        that the sticky DialogHeader/DialogFooter siblings can stick correctly
        relative to the dialog edges instead of fighting with the content's
        scrollable region. This works because Header and Footer use
        `position: sticky` and live as direct children of the same flex
        container as this body wrapper.
      */}
      <div
        className={cn(
          "flex-1 min-h-0 overflow-y-auto overscroll-contain",
          // v77.21.6 — Mobile: only horizontal padding on the body. Top
          // and bottom are zero so the sticky DialogHeader/DialogFooter
          // can fill the full dialog edge naturally without negative-
          // margin gymnastics. The header/footer carry their own
          // padding + safe-area insets.
          "max-sm:px-4 max-sm:py-0",
          // Desktop uses the parent's p-6, so no extra padding here.
        )}
        data-dialog-body=""
      >
        {children}
      </div>

      {!hideCloseButton && (
        <DialogPrimitive.Close
          className={cn(
            "absolute rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100",
            "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none",
            // Slightly larger tap target on mobile, positioned above the
            // safe area so it doesn't disappear under a notch.
            "right-3 top-3 sm:right-4 sm:top-4 p-2 sm:p-0",
            "max-sm:top-[max(0.75rem,env(safe-area-inset-top))]",
            // Sit on top of any sticky header.
            "z-10 bg-background/80 backdrop-blur-sm sm:bg-transparent sm:backdrop-blur-none",
          )}
          data-testid="dialog-close-button"
        >
          <X className="h-5 w-5 sm:h-4 sm:w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

// Header sticks to the top of the scrollable dialog body so the title is
// always visible while the user scrolls a long form. On mobile we also add
// safe-area padding so the title clears the iOS notch.
//
// v77.21.6 — Mobile fix: dropped the `-mt-4` negative margin (which was
// pulling subsequent siblings UP into the header, hiding the first form
// label). The body wrapper now has zero top padding on mobile, so the
// sticky header sits at the natural edge AND the next sibling renders
// cleanly below it. `-mx-4` is retained so the header bg/border still
// spans the full dialog width edge-to-edge. `mb-3` adds breathing room
// between the header and the first form field.
const DialogHeader = ({ className, ...props }) => (
  <div
    data-dialog-header=""
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      // Sticky behaviour — works inside the inner overflow-y-auto wrapper.
      "sticky top-0 z-[5] bg-background",
      // Mobile: full-width banner with bottom border + safe-area top inset.
      "max-sm:-mx-4 max-sm:px-4 max-sm:py-3 max-sm:pr-12 max-sm:mb-3 max-sm:border-b max-sm:border-[#D9E1E3]",
      "max-sm:pt-[max(0.75rem,env(safe-area-inset-top))]",
      // Desktop: just give it a little bottom spacing and no border.
      "sm:pb-2",
      className
    )}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

// Footer sticks to the bottom so submit/cancel buttons are always reachable.
// Buttons stack reversed on mobile (primary on top) for thumb-friendly use.
//
// v77.21.6 — Same fix as the header: dropped the `-mb-4` negative margin
// so the previous sibling (last form field) can never render under the
// sticky footer. `-mx-4` retained for full-width edge appearance.
const DialogFooter = ({ className, ...props }) => (
  <div
    data-dialog-footer=""
    className={cn(
      "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:space-x-2 sm:space-y-0 sm:gap-0",
      // Sticky behaviour.
      "sticky bottom-0 z-[5] bg-background",
      // Mobile: full-width footer with top border + safe-area bottom inset.
      "max-sm:-mx-4 max-sm:px-4 max-sm:py-3 max-sm:mt-3 max-sm:border-t max-sm:border-[#D9E1E3]",
      "max-sm:pb-[max(0.75rem,env(safe-area-inset-bottom))]",
      "sm:pt-2",
      className
    )}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
